import React from 'react';
import {Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import AnimatedSection from '../components/AnimatedSection';

const services=[
  {
    title: "Business Commercial",
    description: "Fortify your physical assets and operations against unforeseen catastrophes with bulletproof policies.",
    icon: FiIcons.FiBriefcase,
    path: "/services/business-commercial"
  },
  {
    title: "General Liability",
    description: "An impenetrable shield against third-party claims of bodily injury,property damage,and personal injury.",
    icon: FiIcons.FiShield,
    path: "/services/general-liability"
  },
  {
    title: "Workers Compensation",
    description: "Protect your workforce and your bottom line while ensuring flawless state compliance.",
    icon: FiIcons.FiUsers,
    path: "/services/workers-compensation"
  },
  {
    title: "Business Owner Policy",
    description: "The ultimate bundled package. Elite property and liability coverage engineered for modern enterprises.",
    icon: FiIcons.FiPackage,
    path: "/services/business-owner-policy"
  }
];

export default function Home() {
  return (
    <div className="bg-white">
      {/* Hero Section */}
      <div className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <motion.img 
            initial={{scale: 1.05}} 
            animate={{scale: 1}} 
            transition={{duration: 2,ease: "easeOut"}} 
            src="https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&w=2069&auto=format&fit=crop" 
            alt="Corporate" 
            className="w-full h-full object-cover" 
          />
          <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-900/90 to-transparent"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full mt-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div 
              initial={{opacity: 0,x: -50}} 
              animate={{opacity: 1,x: 0}} 
              transition={{duration: 0.8,ease: "easeOut"}}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-zinc-800/80 border border-zinc-700 text-zinc-300 text-sm font-bold mb-8 backdrop-blur-md">
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
                Exclusive Commercial Protection
              </div>
              <h1 className="text-5xl md:text-7xl lg:text-8xl font-extrabold text-white leading-[1.1] mb-8 tracking-tighter">
                The Best Kept <br/>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-red-400 drop-shadow-[0_0_15px_rgba(220,38,38,0.5)]">Secret</span> <br/>
                In Insurance.
              </h1>
              <p className="text-xl md:text-2xl text-zinc-400 mb-10 leading-relaxed font-light max-w-xl">
                We don't just sell policies. We architect impenetrable fortresses around your life's work.
              </p>
              
              <div className="flex flex-wrap gap-5">
                <Link to="/contact" className="group relative bg-red-600 text-white px-8 py-4 rounded-full font-bold text-lg transition-all hover:bg-red-700 shadow-[0_0_20px_rgba(220,38,38,0.3)] hover:shadow-[0_0_30px_rgba(220,38,38,0.5)] flex items-center gap-3 overflow-hidden">
                  <span className="relative z-10 flex items-center gap-2">Unlock Your Quote <SafeIcon icon={FiIcons.FiArrowRight} className="group-hover:translate-x-1 transition-transform" /></span>
                </Link>
                <Link to="/about" className="bg-white/5 hover:bg-white/10 text-white backdrop-blur-md border border-white/10 px-8 py-4 rounded-full font-bold text-lg transition-all">
                  Discover Our Method
                </Link>
              </div>
            </motion.div>

            {/* Floating Trust Card */}
            <motion.div 
              initial={{opacity: 0,y: 50}} 
              animate={{opacity: 1,y: 0}} 
              transition={{duration: 1,delay: 0.4}}
              className="hidden lg:block relative"
            >
              <div className="absolute inset-0 bg-red-600/20 blur-[100px] rounded-full"></div>
              <div className="relative bg-zinc-900/80 backdrop-blur-xl border border-zinc-800 p-8 rounded-3xl shadow-2xl ml-auto max-w-sm">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-red-600/20 rounded-full flex items-center justify-center text-red-500">
                    <SafeIcon icon={FiIcons.FiShield} className="text-2xl" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-lg">Elite Protection</h3>
                    <p className="text-zinc-400 text-sm">Top 1% Agency</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  {[
                    {label: "Assets Secured",value: "$10B+"},
                    {label: "Client Retention",value: "99.9%"},
                    {label: "Claims Resolved",value: "< 24 Hrs"}
                  ].map((stat,i)=> (
                    <div key={i} className="flex justify-between items-center border-b border-zinc-800 pb-4 last:border-0 last:pb-0">
                      <span className="text-zinc-400">{stat.label}</span>
                      <span className="text-white font-extrabold">{stat.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Services Section */}
      <section className="py-32 relative bg-zinc-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-red-600 font-bold tracking-widest uppercase text-sm mb-3">Our Arsenal</h2>
            <h3 className="text-4xl md:text-5xl font-extrabold text-zinc-900 mb-6 tracking-tight">Surgical Precision Coverage.</h3>
            <p className="text-zinc-600 text-xl font-light">Bespoke risk mitigation strategies tailored exclusively for industry leaders and visionaries.</p>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service,index)=> (
              <AnimatedSection key={index} delay={index * 0.1}>
                <motion.div 
                  whileHover={{y: -10}}
                  className="bg-white p-8 rounded-3xl shadow-lg shadow-zinc-200/50 border border-zinc-100 h-full flex flex-col group relative overflow-hidden"
                >
                  <div className="absolute top-0 left-0 w-full h-1 bg-red-600 scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300"></div>
                  <div className="w-16 h-16 bg-red-50 text-red-600 rounded-2xl flex items-center justify-center text-3xl mb-8 group-hover:bg-red-600 group-hover:text-white transition-colors duration-300">
                    <SafeIcon icon={service.icon} />
                  </div>
                  <h4 className="text-2xl font-bold text-zinc-900 mb-4 tracking-tight">{service.title}</h4>
                  <p className="text-zinc-600 mb-8 font-light leading-relaxed flex-grow">{service.description}</p>
                  <Link to={service.path} className="text-red-600 font-bold flex items-center gap-2 group-hover:gap-4 transition-all uppercase text-sm tracking-wide">
                    Reveal Strategy <SafeIcon icon={FiIcons.FiArrowRight} />
                  </Link>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-32 bg-zinc-950 text-white relative overflow-hidden">
        <div className="absolute -top-1/2 -right-1/4 w-full h-full bg-red-600/10 blur-[150px] rounded-full pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <AnimatedSection>
              <h2 className="text-red-500 font-bold tracking-widest uppercase text-sm mb-3">The Unfair Advantage</h2>
              <h3 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-8 tracking-tight">Why We Remain a Secret.</h3>
              <p className="text-zinc-400 text-xl mb-10 font-light leading-relaxed">
                Our clients don't want their competitors to know how they mitigate risk so flawlessly. We operate in the shadows to keep your business in the spotlight.
              </p>
              
              <div className="space-y-8">
                {[
                  {title: "Surgical Risk Analysis",desc: "We identify vulnerabilities your current broker didn't even know existed."},
                  {title: "White-Glove Claims",desc: "When disaster strikes,we handle everything. You don't lift a finger."},
                  {title: "Exclusive Markets",desc: "Access to premium underwriting markets unavailable to standard agencies."}
                ].map((item,i)=> (
                  <motion.div whileHover={{x: 10}} key={i} className="flex items-start gap-5 group">
                    <div className="mt-1 bg-zinc-900 border border-zinc-800 text-red-500 p-3 rounded-xl group-hover:bg-red-600 group-hover:text-white transition-colors">
                      <SafeIcon icon={FiIcons.FiUnlock} className="text-xl" />
                    </div>
                    <div>
                      <h4 className="text-xl font-bold text-white mb-2">{item.title}</h4>
                      <p className="text-zinc-400 font-light">{item.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.2} className="relative">
              <div className="grid grid-cols-2 gap-4">
                <motion.img 
                  whileHover={{scale: 1.05}}
                  src="https://images.unsplash.com/photo-1556761175-5973dc0f32b7?q=80&w=1932&auto=format&fit=crop" 
                  alt="Meeting" 
                  className="rounded-3xl object-cover h-64 w-full shadow-2xl transition-transform duration-500" 
                />
                <motion.img 
                  whileHover={{scale: 1.05}}
                  src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?q=80&w=2070&auto=format&fit=crop" 
                  alt="Office" 
                  className="rounded-3xl object-cover h-64 w-full shadow-2xl transition-transform duration-500 mt-12" 
                />
              </div>
              
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-red-600 text-white p-8 rounded-full shadow-[0_0_50px_rgba(220,38,38,0.5)] z-20 w-40 h-40 flex flex-col items-center justify-center text-center border-4 border-zinc-950">
                <div className="text-4xl font-extrabold">3+</div>
                <div className="text-xs font-bold uppercase tracking-wider mt-1">Years</div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>
  );
}